import { MapPin, Briefcase, User, Building2 } from "lucide-react";
import type { WatchlistLeadTrackingRecord } from "../api/WatchlistApi";
import CompanyApplyChip from "shared/CompanyApplyChip";
import RemoteScopeChip from "shared/RemoteScopeChip";

export const LEAD_STATUS_CHIP: Record<string, string> = {
  new:            "bg-blue-500 text-white border-blue-400",
  reviewing:      "bg-violet-500 text-white border-violet-400",
  applied:        "bg-sky-500 text-sky-950 border-sky-400",
  interviewing:   "bg-amber-500 text-amber-950 border-amber-400",
  offer_received: "bg-teal-500 text-white border-teal-400",
  hired:          "bg-emerald-500 text-emerald-950 border-emerald-400",
  rejected:       "bg-red-500 text-white border-red-400",
  not_relevant:   "bg-zinc-500 text-white border-zinc-400",
  archived:       "bg-zinc-700 text-zinc-200 border-zinc-600",
};

export const LEAD_STATUS_LABEL: Record<string, string> = {
  new: "New", reviewing: "Reviewing", applied: "Applied",
  interviewing: "Interviewing", offer_received: "Offer Received",
  hired: "Hired", rejected: "Rejected", not_relevant: "Not Relevant", archived: "Archived",
};

function daysAgo(iso: string | null): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

interface Props {
  lead: WatchlistLeadTrackingRecord;
  onClick: () => void;
  // Slimmer layout for the Kanban board, where columns are narrow and already
  // convey status by grouping — drops the status chip and the duplicated
  // job-type row in favor of one consolidated badge row.
  compact?: boolean;
}

export default function LeadCard({ lead, onClick, compact }: Props) {
  const isPersonal = lead.target_type === "personal";
  const sourceName = lead.target_display_name
    || lead.author_name
    || lead.linkedin_url?.split("/").filter(Boolean).pop()
    || "Unknown source";
  const statusChip = LEAD_STATUS_CHIP[lead.status];
  const statusLabel = LEAD_STATUS_LABEL[lead.status] ?? lead.status;

  if (compact) {
    return (
      <div
        onClick={onClick}
        className="bg-surface/60 border border-border/60 rounded-xl p-3 cursor-pointer hover:border-border transition-all select-none"
      >
        <div className="flex items-start gap-2.5">
          <div className="flex flex-col items-center gap-1 shrink-0">
            <div className="w-8 h-8 rounded-lg border border-border bg-surface-muted flex items-center justify-center">
              {isPersonal
                ? <User className="w-3.5 h-3.5 text-indigo-400" />
                : <Building2 className="w-3.5 h-3.5 text-amber-400" />
              }
            </div>
            {lead.posted_at && (
              <span className="text-[9px] leading-none px-1.5 py-0.5 rounded-full border bg-blue-500 text-white border-blue-400 font-inter whitespace-nowrap animate-pulse">
                {daysAgo(lead.posted_at)}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-foreground font-inter leading-snug line-clamp-2">
              {lead.job_title || "(No job title)"}
            </h3>
            <p className="text-[11px] text-muted-foreground font-inter mt-0.5 truncate">{sourceName}</p>
          </div>
        </div>

        {lead.company_can_apply && (
          <div className="flex flex-wrap items-center gap-1 mt-2">
            <CompanyApplyChip companyCanApply={lead.company_can_apply} metadataJson={lead.company_analysis_metadata} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {lead.job_type && (
            <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter">
              {lead.job_type}
            </span>
          )}
          <RemoteScopeChip remoteScope={lead.remote_scope} analysisJson={lead.remote_worldwide_analysis} className="ml-auto" />
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="bg-surface/60 border border-border/60 rounded-xl p-4 cursor-pointer hover:border-border transition-all select-none"
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          <div className="w-10 h-10 rounded-lg border border-border bg-surface-muted flex items-center justify-center">
            {isPersonal
              ? <User className="w-4.5 h-4.5 text-indigo-400 w-[18px] h-[18px]" />
              : <Building2 className="w-[18px] h-[18px] text-amber-400" />
            }
          </div>
          {lead.posted_at && (
            <span className="text-[9px] leading-none px-1.5 py-0.5 rounded-full border bg-blue-500 text-white border-blue-400 font-inter whitespace-nowrap animate-pulse">
              {daysAgo(lead.posted_at)}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground font-inter leading-snug line-clamp-2">
              {lead.job_title || "(No job title)"}
            </h3>
            <CompanyApplyChip companyCanApply={lead.company_can_apply} metadataJson={lead.company_analysis_metadata} />
            {statusChip && (
              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusChip}`}>
                {statusLabel}
              </span>
            )}
          </div>

          <p className="text-xs text-muted-foreground font-inter mt-0.5 truncate">{sourceName}</p>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
            {lead.job_location && (
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-inter">
                <MapPin className="w-3 h-3" />
                {lead.job_location}
              </span>
            )}
            {lead.job_title && lead.job_type && (
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground font-inter">
                <Briefcase className="w-3 h-3" />
                {lead.job_type}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {lead.job_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted/50 text-muted-foreground border-border/40 font-inter">
                {lead.job_type}
              </span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-inter ${
              isPersonal
                ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/25"
                : "bg-amber-500/15 text-amber-300 border-amber-500/25"
            }`}>
              {isPersonal ? "Personal" : "Company"}
            </span>
            <RemoteScopeChip remoteScope={lead.remote_scope} analysisJson={lead.remote_worldwide_analysis} className="ml-auto" />
          </div>
        </div>
      </div>
    </div>
  );
}
